import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isUndefined } from 'lodash'

import { fetchWallboxChargingStatus } from '../api/charging-session.api'
import { queryFnWithApiResult } from 'src/Libs/api/utils'
import { ApiResult, usePortalMutation, usePortalQuery } from 'src/Libs/api'

type UseActiveChargingSessionStatus = {
  isLoading: boolean
  isChargingSessionPaused: boolean | undefined
  isToggleLoading: boolean
  toggleWallboxChargingStatus: () => Promise<void>
}

interface Props {
  wallboxIndex?: number
  storageId?: string
  enabled?: boolean
  refetchInterval?: number
}

const MAX_REFETCH_ERRORS = 3

/**
 * Hook that returns the status of the current active charging session of a wallbox (CHARGING or WAIT) and a function to toggle the charging session status.
 */
const useActiveChargingSessionStatus = ({ wallboxIndex, storageId, enabled, refetchInterval }: Props): UseActiveChargingSessionStatus => {
  const authFetch = useAuthFetchWithResult()
  const { isLoading, isError, data, refetch } = usePortalQuery<boolean | null>({
    queryKey: ['wallbox-charging-status', storageId, wallboxIndex],
    enabled: enabled && !isUndefined(storageId) && !isUndefined(wallboxIndex),
    queryFn: queryFnWithApiResult(() => fetchWallboxChargingStatus(authFetch, storageId, wallboxIndex)),
    retry: failureCount => failureCount < MAX_REFETCH_ERRORS,
    refetchInterval: ({ state: { fetchFailureCount } }) => {
      if (refetchInterval === undefined || fetchFailureCount >= MAX_REFETCH_ERRORS) return false
      return refetchInterval
    },
  })

  const { isPending: isToggleLoading, mutateAsync: toggleWallboxChargingStatusMutation } = usePortalMutation({
    mutationFn: async () => {
      if (!isUndefined(data) && !isUndefined(storageId) && !isUndefined(wallboxIndex)) {
        await authFetch<void>({
          path: `wallboxes/${storageId}/${wallboxIndex}/charging-session/abort`,
          method: 'POST',
          payload: {
            state: !data,
          },
        })
      }
      return new ApiResult<void>()
    },
    onSuccess: () => {
      refetch()
    },
    onError: error => {
      console.error(error)
    },
  })

  const toggleWallboxChargingStatus = async (): Promise<void> => {
    await toggleWallboxChargingStatusMutation()
  }

  let isChargingSessionPaused: boolean | undefined
  if (!isLoading && !isError && !isUndefined(data)) {
    isChargingSessionPaused = data ?? false
  }

  return {
    isLoading,
    isChargingSessionPaused,
    isToggleLoading,
    toggleWallboxChargingStatus,
  }
}

export default useActiveChargingSessionStatus
